import { Trans, useLingui } from '@lingui/react/macro';

import InlineText from '@/design_system/Typography/InlineText';
import IconAnalysis from '@/icons/Analysis.svg';
import IconItemDelete from '@/icons/ItemDelete.svg';
import { BaseNotification } from '@/layouts/App/Navbar/NotificationList/components/BaseNotification/BaseNotification';
import { useArticleName } from '@/models/article';
import type { TNotificationOfType } from '@/models/notification';

export const RequalificationRefusedNotification = ({
  notification,
}: {
  notification: TNotificationOfType<'requalification_refused'>;
}) => {
  const { t } = useLingui();
  const requestReference = notification.request.reference;
  const articleName = useArticleName({ article: notification.article ?? undefined });

  if (notification.data.nextStep === 'new-diagnosis' || !notification.data.nextStep) {
    return (
      <BaseNotification
        icon={<IconItemDelete />}
        type="danger"
        notification={notification}
        notificationLabel={t({
          id: 'notification.requalification-refused.next-step-new-diagnosis.label',
          message: `Quote refused for request ${requestReference}`,
        })}
        notificationHeader={() => (
          <Trans id="notification.requalification-refused.next-step-new-diagnosis">
            Your quote has been refused
          </Trans>
        )}
      />
    );
  }

  if (notification.data.nextStep === 'new-analysis') {
    return (
      <BaseNotification
        icon={<IconAnalysis />}
        type="new"
        notification={notification}
        notificationLabel={t({
          id: 'notification.requalification-refused.next-step-new-analysis.label',
          message: `A revised quote has been requested for request ${requestReference}`,
        })}
        notificationHeader={(color) => (
          <Trans id="notification.requalification-refused.next-step-new-analysis">
            Your quote for{' '}
            <InlineText weight="medium" color={color}>
              {articleName}
            </InlineText>{' '}
            has been refused and you are asked to submit a revised quote.
          </Trans>
        )}
      />
    );
  }

  return (
    <BaseNotification
      icon={<IconItemDelete />}
      type="danger"
      notification={notification}
      notificationLabel={t({
        id: 'notification.requalification-refused.next-step-return-as-is.label',
        message: `${requestReference} Your quote for ${articleName} has been refused. The item will be returned to the customer without service.`,
      })}
      notificationHeader={(color) => (
        <Trans id="notification.requalification-refused.next-step-return-as-is">
          Your quote for{' '}
          <InlineText weight="medium" color={color}>
            {articleName}
          </InlineText>{' '}
          has been refused. The item will be returned to the customer without service.
        </Trans>
      )}
    />
  );
};
